import {useAtomValue} from 'jotai';
import {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Button} from '@/components/Button';
import toast from '@/components/HotToast';
import {Typography} from '@/components/Typography';
import {useCoordToAddress} from '@/map/api';
import {analysisDrawItemAtom, mapActionModeAtom} from '@/map/atom';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {MapActionTypes} from '@/map/enums';
import {Divider, FlexFullGrow} from '@/styled-components';
import {palette} from '@/styles';
import {copyText} from '@/utils';
import {AnalysisMenu} from './AnaysisMenu';
import {MeasureDistance} from './MeasureDistance';
import {Box} from './StyledComponents';

interface IProps {
  map: naver.maps.Map;
  disabled?: boolean;
}

export const ContextMenu = ({map, disabled: hideMenu}: IProps) => {
  const [eventRefs, setEventRefs] = useState<any[]>([]);

  const drawingItem = useAtomValue(analysisDrawItemAtom);
  const action = useAtomValue(mapActionModeAtom);

  const disabled =
    hideMenu || !!drawingItem || action?.mode === MapActionTypes.ruler;

  const [coords, setCoords] = useState<naver.maps.LatLng>();
  const [position, setPosition] = useState<{left: number; top: number}>();
  const [location, setLocation] = useState<{
    address?: string;
  }>();

  const {data} = useCoordToAddress(
    coords && !location?.address
      ? {lat: coords.lat(), lng: coords.lng()}
      : undefined
  );

  const onRightClick = (e: {
    originalEvent: MouseEvent;
    coord: naver.maps.LatLng;
  }) => {
    const {clientX, clientY} = e.originalEvent;
    setPosition({
      left: clientX,
      top: clientY,
    });
    setCoords(e.coord);
  };

  const clearContextMenu = () => {
    setCoords(undefined);
    setPosition(undefined);
    setLocation(undefined);
  };

  const copyAddress = () => {
    if (!location?.address) {
      return;
    }
    const success = copyText(location.address);

    if (success) {
      toast.success('주소가 복사되었습니다.');
    }
  };

  useEffect(() => {
    if (data?.address) {
      const {address} = data;

      setLocation({
        address,
      });
    }
  }, [data]);

  useEffect(() => {
    setLocation(undefined);
  }, [coords]);

  useEffect(() => {
    if (map && !disabled) {
      setEventRefs((prev) => {
        prev.push(
          naver.maps.Event.addListener(map, 'rightclick', onRightClick)
        );
        prev.push(naver.maps.Event.addListener(map, 'click', clearContextMenu));
        prev.push(naver.maps.Event.addListener(map, 'idle', clearContextMenu));
        return prev;
      });
    }
    if (disabled) {
      eventRefs.forEach((evt) => {
        naver.maps.Event.removeListener(evt);
      });
      setEventRefs([]);
    }

    return () => {
      eventRefs.forEach((evt) => {
        naver.maps.Event.removeListener(evt);
      });
      setEventRefs([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, disabled]);

  return (
    <>
      <Root style={position}>
        <AnalysisMenu coords={coords} clearContextMenu={clearContextMenu} />
        <Divider />
        <MeasureDistance coords={coords} clearContextMenu={clearContextMenu} />
        {!!location?.address?.length && (
          <>
            <Divider />
            <AddressBox>
              <Box>
                <Typography variant="medium12" color={palette.textSecondary}>
                  {location.address}
                </Typography>
              </Box>
              <FlexFullGrow />
              <Button
                variant="ghost"
                color="teal"
                size="small"
                onClick={copyAddress}
              >
                복사
              </Button>
            </AddressBox>
          </>
        )}
      </Root>
    </>
  );
};

const Root = styled.div`
  position: fixed;
  left: -90000px;
  top: -90000px;
  z-index: ${MAP_LAYER_Z_INDEX.MAP_FLOATING};
  width: 150px;
  padding: 0 8px;
  background-color: ${palette.white};
  border: solid 1px ${palette.gray300};
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0px 4px 10px 0px #00000026;
`;

const AddressBox = styled.div`
  display: flex;
  align-items: baseline;
  ${Button} {
    padding-right: 1px;
    flex-shrink: 0;
  }
`;
